import gql from 'graphql-tag';
import { compose, graphql } from 'react-apollo';

import ProductDetail, { IProductDetailProps } from './ProductDetail';

const GetProductsQuery = gql`
    query($productId: String!) {
        product(where: { id: $productId }) {
            id
            title
            description
            metaTitle
            metaDescription
            perex
            slug
            technicalSpecification
            subtitle
            name
            status
            tags
            supplier {
                id
                name
            }
            manufacturer {
                id
                name
            }
            weight
            weightForShipping
            display
            displayStatus
            price
            recommendedPrice
            deliveryPriceAddition
            VATrate
            sku
            categories {
                id
            }
            images {
                id
                priority
                values {
                    id
                    title
                    colorHex
                    attribute {
                        id
                        isVisual
                    }
                }
                file
            }
            files {
                id
                title
                file
                visible
                priority
            }
            videos {
                id
                url
                title
                visible
                priority
            }
            prices {
                id
                productId
                productVariantId
                currencyId
                priceWithout
                recommendedPriceWithout
                discount
                discountPercent
                minimalPrice
                availabilityId
                stockAmount
                outOfStockTransitionStatusId
            }
            variants {
                id
                ean
                sku
                price
                recommendedPrice
                prices
                definition
                attributeValues {
                    id
                    title
                    value
                }
                availability {
                    id
                    title
                    name
                }
            }
            availabilityId
            availability {
                id
                title
            }
            hideForCountry
            visibleForCustomerGroup
            neverApplyCustomerDiscounts
            customerGroupsOnlyDiscountIds
            recommendedProductVariants {
                id
                order
                productVariant {
                    id
                }
            }
            externalBlogTags
            externalBlogArticleNumber
            purchaseLimitPerPurchase
            purchaseLimitPerPeriod
        }
        languages {
            id
            value
        }
    }
`;

const UpdateProductMutation = gql`
    mutation updateProduct(
        $id: String!
        $title: String!
        $description: JSON!
        $metaTitle: JSON
        $metaDescription: JSON
        $subtitle: JSON
        $technicalSpecification: JSON
        $name: JSON!
        $categories: [String!]!
        $variants: [JSON!]
        $VATrate: Int!
        $deliveryPriceAddition: Float
        $display: Boolean!
        $displayStatus: Int!
        $manufacturerId: String!
        $perex: JSON!
        $price: Float
        $recommendedPrice: Float
        $prices: [PriceInput!]
        $slug: JSON!
        $status: Int!
        $tags: [String!]
        $weight: Float
        $weightForShipping: Float
        $supplierId: String!
        $accessories: [AccessoryInput]
        $sku: String
        $availabilityId: String
        $videos: [ProductVideoInput]
        $hideForCountry: [String]
        $visibleForCustomerGroup: [String]
        $neverApplyCustomerDiscounts: Boolean
        $customerGroupsOnlyDiscountIds: [String!]
        $recommendedProductVariantIds: [String!]
        $externalBlogTags: [String!]
        $externalBlogArticleNumber: Int
        $purchaseLimitPerPurchase: Int
        $purchaseLimitPerPeriod: JSON
    ) {
        updateProduct(
            id: $id
            product: {
                title: $title
                description: $description
                name: $name
                metaTitle: $metaTitle,
                metaDescription: $metaDescription,
                technicalSpecification: $technicalSpecification
                subtitle: $subtitle
                categories: $categories
                variants: $variants
                VATrate: $VATrate
                deliveryPriceAddition: $deliveryPriceAddition
                display: $display
                displayStatus: $displayStatus
                manufacturerId: $manufacturerId
                perex: $perex
                price: $price
                recommendedPrice: $recommendedPrice
                prices: $prices
                slug: $slug
                status: $status
                tags: $tags
                weight: $weight
                weightForShipping: $weightForShipping
                supplierId: $supplierId
                accessories: $accessories
                sku: $sku
                availabilityId: $availabilityId
                videos: $videos
                hideForCountry: $hideForCountry
                visibleForCustomerGroup: $visibleForCustomerGroup
                neverApplyCustomerDiscounts: $neverApplyCustomerDiscounts
                customerGroupsOnlyDiscountIds: $customerGroupsOnlyDiscountIds
                recommendedProductVariantIds: $recommendedProductVariantIds
                externalBlogTags: $externalBlogTags
                externalBlogArticleNumber: $externalBlogArticleNumber
                purchaseLimitPerPurchase: $purchaseLimitPerPurchase
                purchaseLimitPerPeriod: $purchaseLimitPerPeriod
            }
        ) {
            id
        }
    }
`;

const CreateProductMutation = gql`
    mutation createProduct(
        $title: String!
        $description: JSON!
        $metaTitle: JSON
        $metaDescription: JSON
        $subtitle: JSON
        $technicalSpecification: JSON
        $name: JSON!
        $categories: [String!]!
        $variants: [JSON!]
        $VATrate: Int!
        $deliveryPriceAddition: Float
        $display: Boolean!
        $displayStatus: Int!
        $manufacturerId: String!
        $perex: JSON!
        $price: Float
        $recommendedPrice: Float
        $prices: [PriceInput!]
        $slug: JSON!
        $status: Int!
        $tags: [String!]
        $weight: Float
        $weightForShipping: Float
        $supplierId: String!
        $sku: String
        $availabilityId: String
        $neverApplyCustomerDiscounts: Boolean
        $customerGroupsOnlyDiscountIds: [String!]
        $externalBlogTags: [String!]
        $externalBlogArticleNumber: Int
    ) {
        createProduct(
            product: {
                title: $title
                description: $description
                name: $name
                metaTitle: $metaTitle
                metaDescription: $metaDescription
                technicalSpecification: $technicalSpecification
                subtitle: $subtitle
                categories: $categories
                variants: $variants
                VATrate: $VATrate
                deliveryPriceAddition: $deliveryPriceAddition
                display: $display
                displayStatus: $displayStatus
                manufacturerId: $manufacturerId
                perex: $perex
                price: $price
                recommendedPrice: $recommendedPrice
                prices: $prices
                slug: $slug
                status: $status
                tags: $tags
                weight: $weight
                weightForShipping: $weightForShipping
                supplierId: $supplierId
                sku: $sku
                availabilityId: $availabilityId
                neverApplyCustomerDiscounts: $neverApplyCustomerDiscounts
                customerGroupsOnlyDiscountIds: $customerGroupsOnlyDiscountIds
                externalBlogTags: $externalBlogTags
                externalBlogArticleNumber: $externalBlogArticleNumber
            }
        ) {
            id
        }
    }
`;

const DeleteProductMutation = gql`
    mutation delete($id: String!) {
        deleteProduct(id: $id) {
            id
        }
    }
`;

const DeleteImageMutation = gql`
    mutation deleteImage($id: String!) {
        deleteImage(id: $id) {
            id
        }
    }
`;

const DeleteFileMutation = gql`
    mutation deleteFile($id: String!) {
        deleteProductFile(id: $id) {
            id
        }
    }
`;

export default compose(
    graphql<IProductDetailProps, GQL.IQuery>(GetProductsQuery, {
        name: 'product',
        options: props => ({
            variables: {
                productId: props.productId
            },
            fetchPolicy: 'network-only'
        })
    }),
    graphql(UpdateProductMutation, {
        name: 'updateProduct'
    }),
    graphql(CreateProductMutation, {
        name: 'createProduct'
    }),
    graphql(DeleteProductMutation, {
        name: 'deleteProduct'
    }),
    graphql(DeleteImageMutation, {
        name: 'removeImage'
    }),
    graphql(DeleteFileMutation, {
        name: 'removeFile'
    })
)(ProductDetail);



// WEBPACK FOOTER //
// ./src/scenes/Products/ProductDetail/index.ts